// Настройки отображения на устройстве (очередь идей 2026-09-17, п. 5):
// размер текста и контраст — для родителей 55+ и чтения на солнце. Без
// сервера: localStorage + применение к <html> (components/display-prefs.tsx).
// Размер — через zoom на корне: в приложении размеры в px (text-[16px]),
// root font-size их не масштабирует, а zoom масштабирует всё вместе с отступами.
import { useSyncExternalStore } from 'react'
import { CONTRAST_KEY, FAB_STEPS_OFF_KEY, SCALE_KEY } from '@/lib/display-prefs-boot'

export type TextScale = 1 | 1.15 | 1.3
export const TEXT_SCALES: { value: TextScale; label: string }[] = [
  { value: 1, label: 'Обычный' },
  { value: 1.15, label: 'Крупнее' },
  { value: 1.3, label: 'Крупный' },
]

const listeners = new Set<() => void>()

// fabStepsOff: шар "Спроси Turkido" убран с экранов с заданиями (урок,
// повторение, тренажёры, чат), на остальных страницах он остаётся: через него
// же пишут "Пожелание" и "Нашёл ошибку".
export type DisplayPrefs = { scale: TextScale; contrast: boolean; fabStepsOff: boolean }

function read(): DisplayPrefs {
  try {
    const raw = Number(window.localStorage.getItem(SCALE_KEY))
    const scale = TEXT_SCALES.some((s) => s.value === raw) ? (raw as TextScale) : 1
    return { scale, contrast: window.localStorage.getItem(CONTRAST_KEY) === '1', fabStepsOff: window.localStorage.getItem(FAB_STEPS_OFF_KEY) === '1' }
  } catch {
    return { scale: 1, contrast: false, fabStepsOff: false }
  }
}

const DEFAULT: DisplayPrefs = { scale: 1, contrast: false, fabStepsOff: false }
let cache: DisplayPrefs | null = null

function snapshot(): DisplayPrefs {
  if (!cache) cache = read()
  return cache
}

export function applyDisplayPrefs(prefs: DisplayPrefs = snapshot()) {
  if (typeof document === 'undefined') return
  const root = document.documentElement as HTMLElement & { style: CSSStyleDeclaration & { zoom?: string } }
  root.style.zoom = prefs.scale === 1 ? '' : String(prefs.scale)
  // Плавающий шар Turkido не растёт вместе с текстом (globals.css, .tk-fab):
  // увеличенный он закрывал текст почти на каждой странице телефона.
  root.classList.toggle('tk-zoom-115', prefs.scale === 1.15)
  root.classList.toggle('tk-zoom-130', prefs.scale === 1.3)
  root.classList.toggle('tk-contrast', prefs.contrast)
}

export function setDisplayPrefs(next: Partial<DisplayPrefs>) {
  const prefs = { ...snapshot(), ...next }
  try {
    if (prefs.scale === 1) window.localStorage.removeItem(SCALE_KEY)
    else window.localStorage.setItem(SCALE_KEY, String(prefs.scale))
    if (prefs.contrast) window.localStorage.setItem(CONTRAST_KEY, '1')
    else window.localStorage.removeItem(CONTRAST_KEY)
    if (prefs.fabStepsOff) window.localStorage.setItem(FAB_STEPS_OFF_KEY, '1')
    else window.localStorage.removeItem(FAB_STEPS_OFF_KEY)
  } catch {}
  cache = prefs
  applyDisplayPrefs(prefs)
  listeners.forEach((cb) => cb())
}

function subscribe(cb: () => void) {
  listeners.add(cb)
  const onStorage = () => {
    cache = null
    cb()
  }
  window.addEventListener('storage', onStorage)
  return () => {
    listeners.delete(cb)
    window.removeEventListener('storage', onStorage)
  }
}

export function useDisplayPrefs(): DisplayPrefs {
  return useSyncExternalStore(subscribe, snapshot, () => DEFAULT)
}
